import { Bell, BellOff, Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usePushNotifications } from "../hooks/usePushNotifications";

interface EnableNotificationsButtonProps {
  className?: string;
}

/**
 * Opt-in button that prompts the user for notification permission and registers
 * the FCM token. Renders nothing when push is unsupported/unconfigured, and
 * reflects the current state (requesting / enabled / denied).
 */
const EnableNotificationsButton = ({ className }: EnableNotificationsButtonProps) => {
  const { t } = useTranslation();
  const { status, isSupported, enableNotifications } = usePushNotifications({
    autoInitialize: true,
  });

  useEffect(() => {}, [isSupported, status]);

  if (!isSupported) {
    return null;
  }

  const isBusy = status === "requesting";
  const isEnabled = status === "enabled";
  const isDenied = status === "denied";

  const label = isEnabled
    ? t("push_notifications.enabled")
    : isDenied
      ? t("push_notifications.blocked")
      : t("push_notifications.enable");

  const handleClick = async () => {
    await enableNotifications();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isBusy || isEnabled || isDenied}
      className={
        className ??
        "inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
      }
      title={label}
    >
      {isBusy ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : isDenied ? (
        <BellOff className="h-4 w-4" />
      ) : (
        <Bell className="h-4 w-4" />
      )}
      <span>{label}</span>
    </button>
  );
};

export default EnableNotificationsButton;
